import React, { useState } from "react";
import {
  MdRefresh,
  MdCrop,
  MdAutoFixHigh,
  MdAddAPhoto,
  MdCheck,
} from "react-icons/md";

/**
 * Preview view showing the processed, perspective-warped scan result.
 * Features 4 bottom buttons in a single bar for maximum ease of use:
 * 1. Retake (rounded pill)
 * 2. Crop (square button smaller)
 * 3. Filters (square button smaller, toggles filter tray)
 * 4. Keep Scanning (rounded pill) / Accept (if single image)
 */
const PreviewView = ({
  blobUrl,
  onAccept,
  onAddPage,
  onRetake,
  onAdjustCrop,
  scanMode,
  onScanModeChange,
  quality,
  isProcessing,
  captureType = "pdf",
}) => {
  const [showFilterTray, setShowFilterTray] = useState(false);

  const qualityLabel =
    quality?.quality === "good"
      ? "✓ Good Quality"
      : quality?.quality === "fair"
      ? "~ Fair Quality"
      : quality?.quality === "poor"
      ? "! Poor Quality"
      : quality?.quality === "critical"
      ? "!! Blurry — Retake?"
      : null;

  return (
    <div className="ds-preview-view">
      {/* Processed scan image area */}
      <div className="ds-preview-image-area">
        {blobUrl ? (
          <img
            src={blobUrl}
            alt="Scanned document"
            className="ds-preview-image"
          />
        ) : (
          <div className="ds-processing-overlay" style={{ position: "relative" }}>
            <div className="ds-processing-spinner" />
            <span className="ds-processing-text">Applying document filter...</span>
          </div>
        )}

        {/* Quality badge */}
        {qualityLabel && blobUrl && (
          <div className={`ds-quality-badge ds-quality-${quality.quality}`}>
            {qualityLabel}
          </div>
        )}

        {/* Processing overlay during mode switch */}
        {isProcessing && blobUrl && (
          <div className="ds-processing-overlay">
            <div className="ds-processing-spinner" />
            <span className="ds-processing-text">Applying filter...</span>
          </div>
        )}
      </div>

      {/* Filter Tray (toggled when Filters button is clicked) */}
      {showFilterTray && (
        <div className="ds-filter-tray">
          {[
            { key: "color", label: "Enhanced" },
            { key: "bw", label: "B&W Scan" },
            { key: "original", label: "Original" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`ds-filter-option ${scanMode === key ? "ds-filter-active" : ""}`}
              onClick={() => onScanModeChange(key)}
              disabled={isProcessing}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* 4 Bottom Action Buttons in a Single Bar */}
      <div className="ds-preview-bottom-bar">
        {/* 1. Retake (rounded) */}
        <button
          type="button"
          className="ds-preview-action-btn ds-btn-pill ds-btn-retake"
          onClick={onRetake}
          disabled={isProcessing}
          title="Retake photo"
        >
          <MdRefresh className="ds-action-icon" />
          <span>Retake</span>
        </button>

        {/* 2. Crop (square smaller) */}
        <button
          type="button"
          className="ds-preview-action-btn ds-btn-square ds-btn-crop"
          onClick={onAdjustCrop}
          disabled={isProcessing}
          title="Adjust borders"
        >
          <MdCrop className="ds-action-icon" />
          <span className="ds-action-label">Crop</span>
        </button>

        {/* 3. Filters (square smaller) */}
        <button
          type="button"
          className={`ds-preview-action-btn ds-btn-square ds-btn-filters ${
            showFilterTray ? "ds-btn-active" : ""
          }`}
          onClick={() => setShowFilterTray((prev) => !prev)}
          disabled={isProcessing}
          title="Choose document filter"
        >
          <MdAutoFixHigh className="ds-action-icon" />
          <span className="ds-action-label">Filters</span>
        </button>

        {/* 4. Keep Scanning (rounded) / Accept Image */}
        <button
          type="button"
          className="ds-preview-action-btn ds-btn-pill ds-btn-keep-scanning"
          onClick={captureType === "image" ? onAccept : onAddPage}
          disabled={isProcessing || !blobUrl}
          title={captureType === "image" ? "Accept Image" : "Save page & scan next"}
        >
          {captureType === "image" ? (
            <>
              <MdCheck className="ds-action-icon" />
              <span>Accept</span>
            </>
          ) : (
            <>
              <MdAddAPhoto className="ds-action-icon" />
              <span>Keep Scanning</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PreviewView;
